import type {
  PanelInterface,
  PanelMapInterface,
  UnitInterface,
} from 'store/units/types';
import { PanelType } from 'store/units/types';
import { isFeed, isMobileUnit } from './units';
import { UnitTypes } from '../../src/unitData/types';

export const getPanelLoc =
  (unit: UnitInterface, locations?: Record<PanelType, string>) =>
  (type: PanelType) => {
    const isMobile = isMobileUnit(unit.type);
    const offsetXLeft = isMobile ? 110 : 62;
    const offsetXRight = isMobile ? 110 : 80;

    const [x, y] = unit.layout.loc.split(' ').map((l) => parseInt(l, 10));

    if (type === PanelType.INFO_PANEL) {
      const panelLoc = locations?.[type]
        ? locations?.[type].split(' ').map((l) => parseInt(l, 10))
        : null;

      if (panelLoc) {
        return `${x - panelLoc[0]} ${y - panelLoc[1]}`;
      }
      if (unit.type === UnitTypes.STORAGE) {
        return `${x + 40} ${y + 80}`;
      } else {
        return `${x - offsetXLeft} ${y}`;
      }
    }

    if (type === PanelType.PARTICLE_SIZE_DISTRIBUTION_LIST_PANEL) {
      const panelLoc = locations?.[type]
        ? locations?.[type].split(' ').map((l) => parseInt(l, 10))
        : null;

      if (panelLoc) {
        return `${x - panelLoc[0]} ${y - panelLoc[1]}`;
      }
      return `${x - offsetXLeft - 180} ${y - 60}`;
    }

    if (
      type === PanelType.WARNINGS_PANEL ||
      PanelType.PARTICLE_SIZE_DISTRIBUTION_GRAPH_PANEL
    ) {
      return `${x + offsetXRight} ${y}`;
    }

    return `${x} ${y + 100}`;
  };

export const getDefaultPanels = (
  unit: UnitInterface,
  panelMap?: Record<PanelType, string>,
): PanelMapInterface => {
  const loc = getPanelLoc(unit, panelMap);

  const infoPanel: PanelInterface = {
    loc: loc(PanelType.INFO_PANEL),
    type: PanelType.INFO_PANEL,
    unitKey: unit.key,
  };

  const warningPanel: PanelInterface = {
    loc: loc(PanelType.WARNINGS_PANEL),
    type: PanelType.WARNINGS_PANEL,
    unitKey: unit.key,
  };

  const flowPropertiesGraphPanel: PanelInterface = {
    loc: loc(PanelType.FLOW_PROPERTIES_GRAPH_PANEL),
    type: PanelType.FLOW_PROPERTIES_GRAPH_PANEL,
    unitKey: unit.key,
    width: 375,
    height: 300,
  };

  const flowPropertiesSizeTablePanel: PanelInterface = {
    loc: loc(PanelType.FLOW_PROPERTIES_SIZE_TABLE_PANEL),
    type: PanelType.FLOW_PROPERTIES_SIZE_TABLE_PANEL,
    unitKey: unit.key,
  };

  const flowPropertiesPxxTablePanel: PanelInterface = {
    loc: loc(PanelType.FLOW_PROPERTIES_PXX_TABLE_PANEL),
    type: PanelType.FLOW_PROPERTIES_PXX_TABLE_PANEL,
    unitKey: unit.key,
  };

  const particleSizeDistributionListPanel: PanelInterface = {
    loc: loc(PanelType.PARTICLE_SIZE_DISTRIBUTION_LIST_PANEL),
    type: PanelType.PARTICLE_SIZE_DISTRIBUTION_LIST_PANEL,
    unitKey: unit.key,
  };

  const particleSizeDistributionGraphPanel: PanelInterface = {
    loc: loc(PanelType.PARTICLE_SIZE_DISTRIBUTION_GRAPH_PANEL),
    type: PanelType.PARTICLE_SIZE_DISTRIBUTION_GRAPH_PANEL,
    unitKey: unit.key,
    width: 375,
    height: 300,
  };

  let panels: Record<string, unknown> = {
    [PanelType.INFO_PANEL]: infoPanel,
    [PanelType.WARNINGS_PANEL]: warningPanel,
    [PanelType.FLOW_PROPERTIES_PXX_TABLE_PANEL]: flowPropertiesPxxTablePanel,
  };

  if (isFeed(unit)) {
    panels = {
      ...panels,
      [PanelType.PARTICLE_SIZE_DISTRIBUTION_LIST_PANEL]:
        particleSizeDistributionListPanel,
      [PanelType.PARTICLE_SIZE_DISTRIBUTION_GRAPH_PANEL]:
        particleSizeDistributionGraphPanel,
    };
  } else {
    panels = {
      ...panels,
      [PanelType.FLOW_PROPERTIES_GRAPH_PANEL]: flowPropertiesGraphPanel,
      [PanelType.FLOW_PROPERTIES_SIZE_TABLE_PANEL]:
        flowPropertiesSizeTablePanel,
    };
  }

  return {
    [unit.key]: panels,
  } as PanelMapInterface;
};
